import React, { useMemo } from "react";

import { Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { colors } from "@zapalacorp/tokens";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { BiPlus } from "react-icons/bi";
import * as yup from "yup";

import { Shipment } from "../../../models/shipments";
import { handleGetConvertValue, MappingNameMetric } from "../../../util/converts";
import {
  ShipmentDetails,
  ShipmentObjects,
  ShipmentType,
  SummaryOptions,
} from "../../../util/enums";
import { DecimalFormatType } from "../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../util/getMeasurement";
import { Color } from "../../Form/Button";
import { ContainerDetails } from "./components/Details";
import { ContainerDetailsView } from "./components/Details/view";
import {
  Title,
  CountBadge,
  AddContainerButton,
  Summary,
  HeaderWrapper,
  HeaderTitle,
  ContainerSummaryTitleTypo,
  ContainerSummarySubtitleTypo,
  ContainerGridHeaderTypo,
} from "./styles";
import { Theme } from "@mui/material/styles";

const useStyles = makeStyles<Theme>((theme) => ({
  formField: {
    [theme.breakpoints.up("md")]: {
      width: (props: { isShowDeleteButton: boolean }) =>
        props.isShowDeleteButton ? `12.3%` : `${100 / 8}%`,
      flexBasis: "auto",
    },
  },
  required: {
    color: "#DC3545",
  },
}));

interface ShipmentContainersProps {
  isView?: boolean;
  shipment?: Shipment;
  errors?: yup.ValidationError[];
  summaryOptions?: SummaryOptions[];
  handleUpdateShipment: (attribute: keyof Shipment | "all", value: any) => void;
  handleChangeOptions: (object: any, noChanges?: boolean) => void;
}

const Containers = ({
  isView = false,
  errors = [],
  shipment,
  handleChangeOptions,
  handleUpdateShipment,
}: ShipmentContainersProps) => {
  const { t: translate } = useTranslation();
  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();
  const deciamlFormatTypeForWeight = measurementWeight?.decimalPlaces || DecimalFormatType.MAERSK;
  const deciamlFormatTypeForVolume = measurement?.decimalPlaces || DecimalFormatType.MAERSK;

  const summaryValues = useMemo(() => {
    const values = {
      totalWeight: 0,
      volumetricWeight: 0,
      grossVolume: 0,
    };
    if (shipment && shipment?.containers) {
      for (const container of shipment.containers) {
        let volumeMetricTemp = container.volumetricWeight || 0;
        if (!volumeMetricTemp) {
          // calculate volumetric weight
          volumeMetricTemp = Number(container?.grossVolume) / 1000;
        }
        values.totalWeight += Number(container.grossWeight || 0);
        values.volumetricWeight += Number(volumeMetricTemp || 0);
        values.grossVolume += Number(container.grossVolume || 0);
      }
    }

    return values;
  }, [shipment?.containers]);

  const handleAddContainer = () => {
    const containers = _.cloneDeep(shipment?.containers || [{}]);

    containers.push({ isNew: true });

    handleUpdateShipment(ShipmentDetails.CONTAINERS, containers);
    handleChangeOptions(ShipmentObjects.CONTAINERS);
  };

  const hasErrors = useMemo(() => {
    if (errors.length) {
      const filteredErrors = errors?.filter((error) => error.path?.includes("containers"));

      return !!filteredErrors.length;
    }

    return false;
  }, [errors]);

  const Header = () => (
    <HeaderWrapper>
      <HeaderTitle className="d-flex">
        <div className="d-flex">
          <Title hasError={hasErrors}>{translate("containers")}</Title>
          <CountBadge>
            <span>{shipment?.containers?.length || 0}</span>
          </CountBadge>
        </div>
        {errors?.map((error) => {
          return shipment?.containers?.map((_, index) => {
            if (error.path?.includes(`containers[${index}]`)) {
              return (
                <span key={index}>
                  {translate("forContainerOnRow")}{" "}
                  {error?.path?.includes(`[${index}]`) ? index + 1 : ""} {translate("column")}{" "}
                  <b>{translate(error?.path?.split(`containers[${index}].`)[1] as string)}</b>:{" "}
                  <i>{error.message}</i>
                </span>
              );
            }

            return null;
          });
        })}
      </HeaderTitle>
      <Summary>
        <div
          style={{
            padding: "8px 16px",
          }}
        >
          <ContainerSummaryTitleTypo>{translate("totalWeight")}</ContainerSummaryTitleTypo>
          <ContainerSummarySubtitleTypo>
            {handleGetConvertValue(
              Number(summaryValues.totalWeight),
              measurementWeight?.code,
              deciamlFormatTypeForWeight
            )}
            {MappingNameMetric[measurementWeight?.code as keyof typeof MappingNameMetric]}
          </ContainerSummarySubtitleTypo>
        </div>
        <div
          style={{
            padding: "8px 16px",
          }}
        >
          <ContainerSummaryTitleTypo>
            {shipment?.shipmentType === ShipmentType.AIR
              ? translate("dimensionalWeight")
              : translate("volumetricWeight")}
          </ContainerSummaryTitleTypo>
          <ContainerSummarySubtitleTypo>
            {handleGetConvertValue(
              Number(summaryValues.volumetricWeight),
              measurementWeight?.code,
              deciamlFormatTypeForWeight
            )}
            {MappingNameMetric[measurementWeight?.code as keyof typeof MappingNameMetric]}
          </ContainerSummarySubtitleTypo>
        </div>
        <div
          style={{
            padding: "8px 16px",
          }}
        >
          <ContainerSummaryTitleTypo>{translate("grossVolume")}</ContainerSummaryTitleTypo>
          <ContainerSummarySubtitleTypo>
            {handleGetConvertValue(
              Number(summaryValues.grossVolume),
              measurement?.code,
              deciamlFormatTypeForVolume
            )}
            {` ${measurement?.code || ""}`}
          </ContainerSummarySubtitleTypo>
        </div>
      </Summary>
    </HeaderWrapper>
  );

  const containers = shipment?.containers?.length ? shipment?.containers : [{}];
  const gridHeaderClassStyles = useStyles({ isShowDeleteButton: containers?.length > 1 });

  return (
    <>
      {Header()}
      <div
        className="d-flex flex-column"
        style={{
          border: `1px solid ${colors.gray200}`,
          background: colors.gray50,
          borderRadius: "12px",
        }}
      >
        <div
          className="container-grid-header"
          style={{
            padding: "8px",
            boxShadow: "0px -1px 0px 0px #CCDEEE inset",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} className={gridHeaderClassStyles.formField}>
              <ContainerGridHeaderTypo>
                {`${translate("containerCase")} #`}
                <span className={gridHeaderClassStyles.required}>{"*"}</span>
              </ContainerGridHeaderTypo>
            </Grid>
            <Grid item xs={12} className={gridHeaderClassStyles.formField}>
              <ContainerGridHeaderTypo>{translate("containerCaseSize")}</ContainerGridHeaderTypo>
            </Grid>
            <Grid item xs={12} className={gridHeaderClassStyles.formField}>
              <ContainerGridHeaderTypo>{translate("sealMarkings")}</ContainerGridHeaderTypo>
            </Grid>
            <Grid item xs={12} className={gridHeaderClassStyles.formField}>
              <ContainerGridHeaderTypo>{translate("grossWeight")}</ContainerGridHeaderTypo>
            </Grid>
            <Grid item xs={12} className={gridHeaderClassStyles.formField}>
              <ContainerGridHeaderTypo>{translate("netWeight")}</ContainerGridHeaderTypo>
            </Grid>
            <Grid item xs={12} className={gridHeaderClassStyles.formField}>
              <ContainerGridHeaderTypo>{translate("tareWeight")}</ContainerGridHeaderTypo>
            </Grid>
            <Grid item xs={12} className={gridHeaderClassStyles.formField}>
              <ContainerGridHeaderTypo>{translate("grossVolume")}</ContainerGridHeaderTypo>
            </Grid>
            <Grid item xs={12} className={gridHeaderClassStyles.formField}>
              <ContainerGridHeaderTypo>{translate("volumetricWeight")}</ContainerGridHeaderTypo>
            </Grid>
          </Grid>
        </div>
        <div>
          {containers?.map((container, index) => (
            <React.Fragment key={index}>
              {isView ? (
                <div
                  style={{
                    borderBottom: `1px solid ${colors.gray200}`,
                    borderRadius: "12px",
                  }}
                >
                  <ContainerDetailsView
                    shipmentContainer={container}
                    isAirType={shipment?.shipmentType === ShipmentType.AIR}
                  />
                </div>
              ) : (
                <div
                  style={{
                    borderBottom: `1px solid ${colors.gray200}`,
                    borderRadius: "12px",
                  }}
                >
                  <ContainerDetails
                    key={index}
                    viewOnly={isView}
                    isShowDeleteButton={containers?.length > 1}
                    errors={errors}
                    index={index}
                    containerInfo={container}
                    shipment={shipment}
                    handleChangeOptions={handleChangeOptions}
                    handleUpdateShipment={handleUpdateShipment}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        {!isView ? (
          <div
            style={{
              width: "fit-content",
            }}
          >
            <AddContainerButton
              onClick={handleAddContainer}
              customColor={Color.DEFAULT}
              startIcon={<BiPlus />}
            >
              {translate("addAnotherContainer")}
            </AddContainerButton>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Containers;
